/**
 * The coverage report as a table: one row per question, one column per operator,
 * with the team's own column first.
 *
 * Pure, and separate from the panel because the only interesting decision here
 * is what to draw when a cell has no calls behind it - which is not zero, and
 * saying so is the difference between "this operator never asks about budget"
 * and "this operator made no calls this month".
 */
import type { CoverageReport } from "../../../../lib/callCoverage";

export interface CoverageCell {
  covered: number;
  reads: number;
  /** covered/reads as 0..1, or null when there were no calls to divide by. */
  share: number | null;
}

export interface MatrixRow {
  id: string;
  question: string;
  /** The whole team over the period. */
  team: CoverageCell;
  /** One per column, in `columns` order. */
  cells: CoverageCell[];
}

export interface MatrixColumn {
  userId: string;
  name: string | null;
  reads: number;
}

export interface CoverageMatrix {
  columns: MatrixColumn[];
  rows: MatrixRow[];
  /** The team's own denominator, repeated here so the header can state it. */
  reads: number;
}

function cell(covered: number, reads: number): CoverageCell {
  return { covered, reads, share: reads > 0 ? covered / reads : null };
}

/**
 * Fold a report into the table.
 *
 * Operators with no scored calls are dropped rather than shown as an empty
 * column. They cannot happen from the server - an operator only appears because
 * a call of theirs was counted - but a column of dashes would be the one thing
 * on this screen that carries no information at all, and it would push the
 * columns that do off the side of a narrow pane.
 */
export function coverageMatrix(report: CoverageReport): CoverageMatrix {
  const columns = report.operators
    .filter((op) => op.reads > 0)
    .map((op) => ({ userId: op.userId, name: op.name, reads: op.reads }));

  const rows = report.questions.map((question) => ({
    id: question.id,
    question: question.question,
    team: cell(question.covered, report.reads),
    cells: columns.map((column) => {
      const op = report.operators.find((o) => o.userId === column.userId);
      return cell(op?.covered[question.id] ?? 0, op?.reads ?? 0);
    }),
  }));

  return { columns, rows, reads: report.reads };
}

/** A share as whole percent, or a dash when there was nothing to divide by. */
export function sharePercent(share: number | null): string {
  return share === null ? "-" : `${Math.round(share * 100)}%`;
}

/**
 * The period a preset means, ending now.
 *
 * Days rather than calendar months on purpose: "this month" on the second of
 * the month is two days of calls presented as a month's worth, and the first
 * thing anybody does with a coverage number is compare it to the last one.
 */
export const COVERAGE_DAYS = [7, 30, 90] as const;
export type CoverageDays = (typeof COVERAGE_DAYS)[number];

export function coveragePeriod(days: CoverageDays, now: Date): { from: Date; to: Date } {
  return { from: new Date(now.getTime() - days * 24 * 60 * 60 * 1000), to: now };
}
